import React, { useState, useEffect, useRef } from 'react';
import api from '../services/api';
import Swal from 'sweetalert2';
import { Icon } from '@iconify/react';
import { Card, Row, Col, Form, Button, Badge, Modal, Tab, Nav, Table, Tabs, Dropdown, Container } from 'react-bootstrap';
import { useAuth } from '../context/AuthContext';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import NotificationCenter from '../components/NotificationCenter';
import PatientInvoiceDetailModal from '../components/PatientInvoiceDetailModal';
import Sidebar from '../components/Sidebar';
import Header from '../components/Header';
import SymptomChecker from '../components/SymptomChecker';

const PatientDashboard = () => {
    const { user, logout } = useAuth();
    const navigate = useNavigate();
    const queueScrollRef = useRef(null); // Ref for Queue Scroll

    // UI State (Template Specific)
    const [sidebarActive, setSidebarActive] = useState(false);
    const [mobileMenu, setMobileMenu] = useState(false);

    // View State
    const [view, setView] = useState('overview');

    // Data State
    const [doctors, setDoctors] = useState([]);
    const [clinics, setClinics] = useState([]);
    const [specializations, setSpecializations] = useState([]);
    const [locations, setLocations] = useState([]);
    const [appointments, setAppointments] = useState([]);
    const [activeAppt, setActiveAppt] = useState(null);
    const [queueStats, setQueueStats] = useState({ currentServing: 0 });

    // Search & Filter State (Find Doctor)
    const [searchQuery, setSearchQuery] = useState('');
    const [filters, setFilters] = useState({ clinicId: '', specialization: '', location: '', experienceMin: 0 });

    // MY APPOINTMENTS FILTERS
    const [myApptFilters, setMyApptFilters] = useState({ search: '', date: '', doctorId: '', status: '' });
    const [recordsFilters, setRecordsFilters] = useState({ search: '', date: '' });
    const [rescheduleFilters, setRescheduleFilters] = useState({ doctor: '', date: '', time: '' });


    // Invoices State
    const [invoices, setInvoices] = useState([]);
    const [invoiceFilters, setInvoiceFilters] = useState({ search: '', date: '' });
    const [showInvoiceModal, setShowInvoiceModal] = useState(false);
    const [selectedInvoice, setSelectedInvoice] = useState(null);

    // Booking State
    const [showBookingModal, setShowBookingModal] = useState(false);
    const [showDoctorDetailsModal, setShowDoctorDetailsModal] = useState(false);
    const [selectedDoctor, setSelectedDoctor] = useState(null);
    const [bookingDate, setBookingDate] = useState(new Date().toISOString().split('T')[0]);
    const [rescheduleApptId, setRescheduleApptId] = useState(null); // Track ID for rescheduling
    const [availableSlots, setAvailableSlots] = useState([]);
    const [selectedSlot, setSelectedSlot] = useState('');
    const [consultationType, setConsultationType] = useState('Physical');
    const [isLoadingSlots, setIsLoadingSlots] = useState(false);

    // Records & Profile
    const [showRxModal, setShowRxModal] = useState(false);
    const [viewRx, setViewRx] = useState(null);
    const [profileForm, setProfileForm] = useState({ name: '', phone: '', email: '' });
    const [passwordForm, setPasswordForm] = useState({ currentPassword: '', newPassword: '', confirmPassword: '' });

    // SYMPTOM CHECKER STATE
    const [fromSymptomChecker, setFromSymptomChecker] = useState(false);
    const [isFetchingDoctors, setIsFetchingDoctors] = useState(false);

    const handleFindDoctorForSymptom = (specialization) => {
        // LOGIC REMOVE REQUEST: Just redirect to Find Doctor section without applying filters.
        setFilters({ ...filters, specialization: '' }); // Ensure no specific filter is stuck
        setSearchQuery('');
        navigate('?view=find_doctor');
    };



    const handleViewMap = (url) => {
        if (url) {
            window.open(url, '_blank', 'noreferrer');
        } else {
            Swal.fire({
                icon: 'info',
                title: 'Location Not Available',
                text: 'The map location for this clinic is not currently available in our database.',
                confirmButtonColor: '#0d6efd'
            });
        }
    };

    // Profile Modal
    // Profile Modal (Removed for Tab View)

    // --- INITIALIZATION ---
    // Handle URL Params
    const location = useLocation();
    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const viewParam = params.get('view');
        if (viewParam) {
            setView(viewParam);
        } else {
            setView('overview');
        }
    }, [location.search]);

    useEffect(() => {
        if (user) {
            setProfileForm({ name: user.name, phone: user.phone || '', email: user.email });
            fetchInitialData();
        }
    }, [user]);

    useEffect(() => {
        const interval = setInterval(() => {
            if (activeAppt && activeAppt.status === 'Active' && activeAppt.doctorId?._id) {
                fetchQueueStatus(activeAppt.doctorId._id);
            }
        }, 5000);
        return () => clearInterval(interval);
    }, [activeAppt]);

    useEffect(() => {
        if (view === 'find_doctor') fetchDoctors();
        if (view === 'invoices') fetchInvoices();
    }, [searchQuery, filters, view]);

    useEffect(() => {
        if (selectedDoctor && bookingDate) {
            fetchSlots(selectedDoctor._id, bookingDate);
        }
    }, [bookingDate, selectedDoctor]);

    // Symptom Checker Fallback Logic - REMOVED PER USER REQUEST
    // useEffect(() => {
    //     if (view === 'find_doctor' && fromSymptomChecker && !isFetchingDoctors) {
    //         if (doctors.length === 0 && filters.specialization) {
    //              // ... popup logic ...
    //         }
    //     }
    // }, [view, fromSymptomChecker, isFetchingDoctors, doctors, filters]);

    const fetchInitialData = () => {
        fetchAppointments();
        fetchClinics();
        fetchSpecializations();
        fetchLocations();
        fetchDoctors(); // Pre-fetch doctors for rescheduling dropdown
    };

    // --- API ACTIONS ---
    const fetchDoctors = async () => {
        setIsFetchingDoctors(true);
        try {
            const token = localStorage.getItem('token');
            const params = { ...filters };
            if (searchQuery) params.query = searchQuery;
            const res = await api.get('/public/doctors', { params });
            setDoctors(res.data);
        } catch (e) { console.error("Fetch Doc Error", e); }
        finally { setIsFetchingDoctors(false); }
    };

    const fetchClinics = async () => {
        try { const res = await api.get('/public/clinics'); setClinics(res.data); } catch (e) { }
    };

    const fetchSpecializations = async () => {
        try { const res = await api.get('/public/specializations'); setSpecializations(res.data); } catch (e) { }
    };

    const fetchLocations = async () => {
        try { const res = await api.get('/public/locations'); setLocations(res.data); } catch (e) { }
    };

    const fetchAppointments = async () => {
        try {
            const res = await api.get('/appointments');
            setAppointments(res.data);
            const today = new Date().toDateString();
            const active = res.data.find(a => new Date(a.date).toDateString() === today && ['Active'].includes(a.status));
            setActiveAppt(active || null);
            if (active?.doctorId?._id) fetchQueueStatus(active.doctorId._id);
        } catch (e) { }
    };

    const fetchSlots = async (docId, date) => {
        setIsLoadingSlots(true);
        setAvailableSlots([]);
        try {
            const res = await api.get(`/public/v2/doctors/${docId}/slots`, {
                params: { date },
            });
            setAvailableSlots(res.data);
        } catch (e) { console.error(e); }
        finally { setIsLoadingSlots(false); }
    };

    const fetchQueueStatus = async (doctorId) => {
        try {
            const res = await api.get('/queue/status', { params: { doctorId } });
            if (res.data) setQueueStats({ currentServing: res.data.currentServingToken || 0 });
        } catch (e) { }
    };

    const loadRazorpay = () => {
        return new Promise((resolve) => {
            const script = document.createElement('script');
            script.src = 'https://checkout.razorpay.com/v1/checkout.js';
            script.onload = () => resolve(true);
            script.onerror = () => resolve(false);
            document.body.appendChild(script);
        });
    };

    const handleBooking = async (e) => {
        e.preventDefault();
        if (!selectedSlot) return Swal.fire('Error', 'Please select a time slot', 'error');

        // SMART RESCHEDULING LOGIC
        if (rescheduleApptId) {
            const oldAppt = appointments.find(a => a._id === rescheduleApptId);
            // Determine Fault: If reason specifically mentions doctor/clinic unavailability
            const isDoctorFault = oldAppt && oldAppt.cancellationReason && (
                (oldAppt.cancellationReason.toLowerCase().includes('doctor') ||
                    oldAppt.cancellationReason.toLowerCase().includes('clinic') ||
                    oldAppt.cancellationReason.toLowerCase().includes('unavailable')) &&
                !oldAppt.cancellationReason.toLowerCase().includes('patient') &&
                !oldAppt.cancellationReason.toLowerCase().includes('user')
            );

            if (isDoctorFault) {
                // FREE RESCHEDULE FLOW
                try {
                    const confirm = await Swal.fire({
                        title: 'Confirm Free Reschedule',
                        text: 'Since this appointment was cancelled by the doctor/clinic, you can reschedule for free.',
                        icon: 'info',
                        showCancelButton: true,
                        confirmButtonText: 'Reschedule Now',
                        confirmButtonColor: '#0d6efd'
                    });

                    if (confirm.isConfirmed) {
                        Swal.fire({ title: 'Processing...', didOpen: () => Swal.showLoading() });

                        await api.post('/appointments/reschedule-free', {
                            oldAppointmentId: rescheduleApptId,
                            date: bookingDate,
                            timeSlot: selectedSlot
                        });

                        Swal.fire('Success', 'Appointment Rescheduled Successfully!', 'success');
                        setShowBookingModal(false);
                        setRescheduleApptId(null);
                        fetchAppointments();
                    }
                } catch (err) {
                    Swal.fire('Error', err.response?.data?.message || 'Reschedule failed', 'error');
                }
                return; // Stop execution here (skip payment)
            } else {
                // PAID RESCHEDULE FLOW (User Fault - 50% Fee)
                const confirm = await Swal.fire({
                    title: 'Rescheduling Fee',
                    text: 'A 50% cancellation fee applies for rescheduling this appointment.',
                    icon: 'warning',
                    showCancelButton: true,
                    confirmButtonText: 'Pay & Reschedule',
                    confirmButtonColor: '#0d6efd'
                });
                if (!confirm.isConfirmed) return;
            }
        }

        // STANDARD BOOKING / PAID RESCHEDULE FLOW
        try {
            // Validate Clinic ID before request
            if (!selectedDoctor?.clinicId?._id) {
                return Swal.fire('Error', 'Clinic information missing for this doctor. Please contact support.', 'error');
            }

            // 1. Initiate Payment
            const res = await api.post('/patient/payment/initiate', {
                clinicId: selectedDoctor.clinicId._id,
                doctorId: selectedDoctor._id,
                date: bookingDate,
                timeSlot: selectedSlot,
                type: consultationType,
                rescheduleAppointmentId: rescheduleApptId // Optional: only if rescheduling
            });

            if (!res.data || !res.data.orderId) {
                throw new Error(res.data?.message || "Failed to initiate payment. No Order ID returned.");
            }

            const { orderId, amount, currency, keyId, prefill } = res.data;

            // 2. Open Razorpay
            const options = {
                key: keyId,
                amount: amount * 100, // Amount is in Paise for Display/Validation
                currency: currency,
                name: "Medoro Clinic",
                description: rescheduleApptId ? "Rescheduling Fee" : "Consultation Fee",
                order_id: orderId,
                prefill: prefill,
                theme: { color: "#0d6efd" },
                handler: async function (response) {
                    try {
                        // Show Loading
                        Swal.fire({
                            title: 'Processing Payment',
                            text: 'Verifying payment and confirming your appointment...',
                            allowOutsideClick: false,
                            didOpen: () => {
                                Swal.showLoading();
                            }
                        });

                        // 3. Complete Payment & Confirm Booking
                        await api.post('/patient/payment/complete', {
                            razorpay_payment_id: response.razorpay_payment_id,
                            razorpay_order_id: response.razorpay_order_id,
                            razorpay_signature: response.razorpay_signature,
                            bookingDetails: {
                                clinicId: selectedDoctor.clinicId._id,
                                doctorId: selectedDoctor._id,
                                date: bookingDate,
                                timeSlot: selectedSlot,
                                type: consultationType,
                                oldAppointmentId: rescheduleApptId // Pass for backend to mark old as completed
                            }
                        });

                        Swal.fire({
                            icon: 'success',
                            title: 'Booking Confirmed!',
                            text: 'Payment successful. View your invoice in "My Invoices".',
                            timer: 3000,
                            showConfirmButton: false
                        });

                        setShowBookingModal(false);
                        setRescheduleApptId(null);
                        fetchAppointments(); // Refresh

                    } catch (err) {
                        console.error("Payment Capture Error", err);
                        Swal.fire('Error', err.response?.data?.message || 'Payment successful but booking confirmation failed. Please contact support.', 'error');
                    }
                },
                modal: {
                    ondismiss: function () {
                        Swal.fire('Cancelled', 'Payment process cancelled. Booking not confirmed.', 'info');
                    }
                }
            };

            const rzp1 = new window.Razorpay(options);
            rzp1.on('payment.failed', function (response) {
                Swal.fire('Payment Failed', response.error.description || 'Transaction failed', 'error');
            });
            rzp1.open();

        } catch (error) {
            console.error("Booking Error", error);
            Swal.fire('Error', error.response?.data?.message || error.message || 'Failed to initiate booking process.', 'error');
        }
    };

    // INVOICE ACTIONS
    const fetchInvoices = async () => {
        try {
            const res = await api.get('/patient/invoices');
            setInvoices(res.data);
        } catch (e) { console.error("Fetch Invoices Failed", e); }
    };

    const handleViewInvoice = (inv) => {
        setSelectedInvoice(inv);
        setShowInvoiceModal(true);
    };

    const handleEmailInvoice = async (id) => {
        try {
            const { value: emailResult } = await Swal.fire({
                title: 'Send Invoice via Email',
                input: 'email',
                inputLabel: 'Enter email address',
                inputPlaceholder: 'name@example.com',
                inputValue: user?.email || '', // Default to user's registered email
                showCancelButton: true,
                confirmButtonText: 'Send Email',
                confirmButtonColor: '#0d6efd',
                showLoaderOnConfirm: true,
                allowOutsideClick: () => !Swal.isLoading(),
                preConfirm: async (email) => { // Async preConfirm for loading state
                    if (!email) {
                        Swal.showValidationMessage('Please enter an email address');
                        return false;
                    }
                    try {
                        const res = await api.post(`/patient/invoices/${id}/email`, { targetEmail: email });
                        return { email, message: res.data.message };
                    } catch (error) {
                        Swal.showValidationMessage(
                            `Request failed: ${error.response?.data?.message || 'Unknown error'}`
                        );
                        return false; // Prevent closing
                    }
                }
            });

            if (emailResult && emailResult.email) {
                Swal.fire({
                    icon: 'success',
                    title: 'Email Sent!',
                    text: `Invoice receipt has been sent to ${emailResult.email}`,
                    timer: 3000,
                    showConfirmButton: false
                });
            }
        } catch (e) {
            console.error(e);
            Swal.fire('Error', 'Failed to send email. Please try again.', 'error');
        }
    };



    const fetchPrescription = async (apptId) => {
        try {
            const res = await api.get(`/prescriptions/${apptId}`);
            if (res.data && Object.keys(res.data).length > 0) {
                setViewRx(res.data);
                setShowRxModal(true);
            } else {
                Swal.fire({
                    icon: 'info',
                    title: 'Prescription Not Available',
                    text: 'The doctor has not uploaded a prescription for this appointment yet.',
                    confirmButtonColor: '#0d6efd'
                });
            }
        } catch (e) {
            if (e.response && e.response.status === 404) {
                Swal.fire({
                    icon: 'info',
                    title: 'Prescription Not Available',
                    text: 'No prescription record found for this appointment.',
                    confirmButtonColor: '#0d6efd'
                });
            } else {
                Swal.fire('Error', 'Could not open prescription. Please try again later.', 'error');
            }
        }
    };

    const handleCancel = async (apptId) => {
        try {
            const result = await Swal.fire({
                title: 'Are you sure?',
                text: "You won't be able to revert this! Queue position will be lost.",
                icon: 'warning',
                showCancelButton: true,
                confirmButtonColor: '#d33',
                cancelButtonColor: '#3085d6',
                confirmButtonText: 'Yes, cancel it!'
            });

            if (result.isConfirmed) {
                await api.put(`/appointments/${apptId}/cancel`, {});
                Swal.fire('Cancelled!', 'Your appointment has been cancelled.', 'success');
                fetchAppointments(); // Refresh data
            }
        } catch (error) {
            Swal.fire('Error', error.response?.data?.message || 'Cancellation failed', 'error');
        }
    };

    const handleUpdateProfile = async (e) => {
        e.preventDefault();
        try {
            await api.put('/users/profile', profileForm);
            Swal.fire('Success', 'Profile Updated', 'success');
        } catch (e) { Swal.fire('Error', 'Update failed', 'error'); }
    };

    const handleChangePassword = async (e) => {
        e.preventDefault();
        if (passwordForm.newPassword !== passwordForm.confirmPassword) {
            return Swal.fire('Error', 'New passwords do not match', 'error');
        }
        try {
            await api.put('/users/change-password', {
                currentPassword: passwordForm.currentPassword,
                newPassword: passwordForm.newPassword
            });
            Swal.fire('Success', 'Password Changed Successfully', 'success');
            setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
        } catch (e) {
            Swal.fire('Error', e.response?.data?.message || 'Failed to change password', 'error');
        }
    };

    const getInitials = (n) => n ? n.charAt(0).toUpperCase() : 'U';

    // --- TEMPLATE LAYOUT RENDER ---
    return (
        <section className={mobileMenu ? "overlay active" : "overlay "}>
            {/* SIDEBAR */}
            <Sidebar
                isSidebarActive={sidebarActive}
                mobileMenu={mobileMenu}
                closeSidebar={() => setMobileMenu(false)}
            />

            {/* MAIN CONTENT */}
            <main className={sidebarActive ? "dashboard-main active" : "dashboard-main"}>
                <Header
                    toggleSidebar={() => setSidebarActive(!sidebarActive)}
                    mobileMenuControl={() => setMobileMenu(!mobileMenu)}
                />

                <div className='dashboard-main-body'>
                    {/* VIEW: OVERVIEW */}
                    {view === 'overview' && (
                        <Container fluid className="px-0">
                            <div className="d-flex flex-wrap align-items-center justify-content-between gap-3 mb-24">
                                <h6 className="fw-semibold mb-0">Patient Dashboard</h6>
                                <div className="d-flex gap-2">
                                    <button onClick={() => setView('symptom_checker')} className="btn btn-outline-primary-600 radius-8 px-20 py-11 d-flex align-items-center gap-2">
                                        <Icon icon="solar:stethoscope-bold" /> Symptom Checker
                                    </button>
                                    <Link to="#" onClick={() => setView('find_doctor')} className="btn btn-primary-600 radius-8 px-20 py-11">Book New Appointment</Link>
                                </div>
                            </div>

                            {/* QUEUE CARDS CAROUSEL (HORIZONTAL SCROLL) */}
                            {(() => {
                                const todayStr = new Date().toDateString();
                                let todaysAppts = appointments.filter(a => new Date(a.date).toDateString() === todayStr && ['Upcoming', 'Active', 'Completed'].includes(a.status));

                                if (todaysAppts.length === 0) return null;

                                // SORTING LOGIC: Active > Upcoming > Completed | Then Queue Number ASC
                                todaysAppts.sort((a, b) => {
                                    const priority = { 'Active': 1, 'Upcoming': 2, 'Completed': 3 };
                                    const pA = priority[a.status] || 4;
                                    const pB = priority[b.status] || 4;
                                    if (pA !== pB) return pA - pB;
                                    return (a.queueNumber || 0) - (b.queueNumber || 0);
                                });

                                const scrollLeft = () => queueScrollRef.current?.scrollBy({ left: -400, behavior: 'smooth' });
                                const scrollRight = () => queueScrollRef.current?.scrollBy({ left: 400, behavior: 'smooth' });

                                return (
                                    <div className="mb-40 position-relative">
                                        <div className="d-flex align-items-center justify-content-between mb-20">
                                            <div>
                                                <h5 className="fw-bold mb-1 text-primary-900">Today's Appointments</h5>
                                                <p className="text-secondary-light text-sm mb-0">Track your live queue status</p>
                                            </div>
                                            <div className="d-flex align-items-center gap-3">
                                                <span className="badge bg-white text-dark border border-neutral-200 radius-pill px-3 py-2 fw-medium shadow-xs">
                                                    {todaysAppts.length} Scheduled
                                                </span>
                                                {/* ARROWS */}
                                                {todaysAppts.length > 1 && (
                                                    <div className="d-flex gap-2">
                                                        {/* <button
                                                            onClick={scrollLeft}
                                                            className="btn bg-white shadow-sm radius-circle w-32-px h-32-px flex-center border hover-scale-1"
                                                            style={{ color: '#198754', borderColor: '#198754' }}
                                                        >
                                                            <Icon icon="solar:alt-arrow-left-bold" />
                                                        </button>
                                                        <button
                                                            onClick={scrollRight}
                                                            className="btn bg-white shadow-sm radius-circle w-32-px h-32-px flex-center border hover-scale-1"
                                                            style={{ color: '#198754', borderColor: '#198754' }}
                                                        >
                                                            <Icon icon="solar:alt-arrow-right-bold" />
                                                        </button> */}
                                                    </div>
                                                )}
                                            </div>
                                        </div>

                                        {/* SCROLL CONTAINER */}
                                        <div
                                            ref={queueScrollRef}
                                            className="queue-scroll-container d-flex gap-4 pb-4 px-1"
                                            style={{
                                                overflowX: 'auto',
                                                scrollBehavior: 'smooth',
                                                scrollbarWidth: 'none',
                                                msOverflowStyle: 'none'
                                            }}
                                        >
                                            <style>{`
                                                .queue-scroll-container::-webkit-scrollbar { display: none; }
                                            `}</style>

                                            {todaysAppts.map(appt => {
                                                const current = queueStats.currentServing || 0;
                                                const myQueue = appt.queueNumber || 0;
                                                // Status Configuration
                                                let statusConfig = { bg: 'bg-primary-600', icon: 'solar:calendar-mark-bold-duotone', title: 'Upcoming', desc: 'Your turn is coming up soon.' };
                                                if (appt.status === 'Active') {
                                                    statusConfig = { bg: 'bg-danger-500', icon: 'solar:bell-bing-bold-duotone', title: 'Now Serving', desc: 'Please proceed to the doctor\'s room.' };
                                                    if (appt.type === 'Online') statusConfig.desc = 'Doctor is ready. Please join the call.';
                                                } else if (appt.status === 'Completed') {
                                                    statusConfig = { bg: 'bg-success-600', icon: 'solar:check-circle-bold-duotone', title: 'Completed', desc: 'Appointment finished successfully.' };
                                                }

                                                const position = (appt.status === 'Upcoming' || appt.status === 'Active') ? (myQueue - current) : 0;
                                                const isTurn = appt.status === 'Active';

                                                return (
                                                    <div key={appt._id} style={{ minWidth: '40%', maxWidth: '40%', flex: '0 0 auto' }}>
                                                        <div className={`card h-100 border-0 radius-16 shadow-lg overflow-hidden relative ${statusConfig.bg} hover-scale-sm transition-2`}>
                                                            {/* REMOVED GRADIENT OVERLAY */}
                                                            <Icon icon={statusConfig.icon} className="absolute -bottom-12 -right-12 text-white opacity-10 text-[14rem] pointer-events-none" />

                                                            <div className="card-body p-24 relative z-1 d-flex flex-column h-100">
                                                                <div className="d-flex justify-content-between align-items-start mb-auto">
                                                                    <div>
                                                                        <div className="d-flex align-items-center gap-2 mb-3">
                                                                            <span className="badge bg-white-opacity-20 text-white radius-8 px-12 py-6 fw-bold backdrop-blur-md border border-white-opacity-10">
                                                                                Token #{myQueue}
                                                                            </span>
                                                                            <span className="badge bg-white text-dark radius-pill px-3 py-1 text-xs fw-bold shadow-sm">
                                                                                <Icon icon={appt.type === 'Online' ? "solar:videocamera-record-bold" : "solar:hospital-bold"} className="me-1 align-text-bottom" />
                                                                                {appt.type}
                                                                            </span>
                                                                        </div>
                                                                        <h4 className="text-white fw-bold mb-2">{statusConfig.title}</h4>
                                                                        <p className="text-white opacity-90 mb-0 text-md lh-base">{statusConfig.desc}</p>
                                                                    </div>
                                                                    <div className="text-end text-white">
                                                                        {appt.doctorId?.profileImage ? (
                                                                            <div className="w-56-px h-56-px radius-circle bg-white border border-2 border-white ms-auto mb-2 overflow-hidden shadow-sm">
                                                                                <img src={appt.doctorId.profileImage} alt="Dr" className="w-100 h-100 object-fit-cover" />
                                                                            </div>
                                                                        ) : (
                                                                            <div className="w-56-px h-56-px radius-circle bg-white text-primary-600 flex-center text-2xl fw-bold shadow-md mb-2 ms-auto border border-white-opacity-10">
                                                                                {getInitials(appt.doctorId?.name)}
                                                                            </div>
                                                                        )}
                                                                        <p className="fw-bold mb-0 text-md">Dr. {appt.doctorId?.name.replace("Dr.", "")?.replace("Mr.", "")?.replace("Mrs.", "")?.replace("dr.", "")}</p>
                                                                        <p className="opacity-75 text-xs mb-0">{appt.clinicId?.name}</p>
                                                                    </div>
                                                                </div>

                                                                <div className="mt-24 pt-3 border-top border-white-opacity-20">
                                                                    {appt.status !== 'Completed' ? (
                                                                        <>
                                                                            <div className="d-flex justify-content-between text-sm text-white opacity-90 mb-2 font-monospace">
                                                                                {position > 0 && <span><strong>{position}</strong> people ahead</span>}
                                                                            </div>
                                                                            <div className="progress bg-white-opacity-10 radius-pill overflow-hidden shadow-inner" style={{ height: '8px' }}>
                                                                                <div className={`progress-bar bg-white radius-pill ${isTurn ? 'progress-bar-striped progress-bar-animated' : ''}`} role="progressbar" style={{ width: `${Math.max(5, (1 - (position / 10)) * 100)}%` }}></div>
                                                                            </div>

                                                                            {/* ONLINE MEETING LINK */}
                                                                            {appt.status === 'Active' && appt.type === 'Online' && (
                                                                                <div className="mt-3 d-grid">
                                                                                    {appt.meetingLink ? (
                                                                                        <a href={appt.meetingLink} target="_blank" rel="noopener noreferrer" className="btn btn-light text-primary-600 fw-bold radius-12 py-2 shadow-lg pulse-animation d-flex align-items-center justify-content-center gap-2">
                                                                                            <Icon icon="solar:videocamera-record-bold-duotone" className="text-xl" /> Join Video Consultation
                                                                                        </a>
                                                                                    ) : (
                                                                                        <div className="bg-white-opacity-20 text-white radius-12 py-2 text-center fw-medium border border-white-opacity-10">
                                                                                            <Icon icon="solar:clock-circle-linear" className="me-2 text-lg align-text-bottom" />
                                                                                            Waiting for doctor to start meeting...
                                                                                        </div>
                                                                                    )}
                                                                                </div>
                                                                            )}
                                                                        </>
                                                                    ) : (
                                                                        <div className="d-grid">
                                                                            <button className="btn btn-light text-primary-700 w-100 radius-10 fw-bold shadow-md d-flex align-items-center justify-content-center gap-2 py-2.5" onClick={() => fetchPrescription(appt._id)}>
                                                                                <Icon icon="solar:document-medicine-bold-duotone" className="text-xl" /> View Prescription
                                                                            </button>
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>
                                );
                            })()}

                            <div className="row gy-4">
                                {appointments.length === 0 && <div className="col-12 text-center py-5 text-secondary-light">No appointments found.</div>}
                                {[
                                    { label: 'Confirmed', count: appointments.filter(a => ['Upcoming', 'Active', 'Accepted'].includes(a.status)).length, bg: 'bg-info-50', text: 'text-info-600', icon: 'solar:calendar-mark-bold-duotone' },
                                    { label: 'Completed', count: appointments.filter(a => a.status === 'Completed').length, bg: 'bg-success-50', text: 'text-success-600', icon: 'solar:history-bold-duotone' },
                                    { label: 'Cancelled', count: appointments.filter(a => a.status === 'Cancelled').length, bg: 'bg-danger-50', text: 'text-danger-600', icon: 'solar:close-circle-bold-duotone' },
                                ].map((stat, i) => (
                                    <div className="col-xxl-4 col-sm-6" key={i}>
                                        <div className="card h-100 radius-12 border-0 shadow-sm transition-2 hover-scale-1">
                                            <div className="card-body p-24 d-flex align-items-center gap-3">
                                                <div className={`w-64-px h-64-px radius-12 flex-center text-4xl ${stat.bg} ${stat.text}`}>
                                                    <Icon icon={stat.icon} />
                                                </div>
                                                <div>
                                                    <h4 className="mb-0 fw-bold">{stat.count}</h4>
                                                    <span className="text-secondary-light text-md">{stat.label} Appointments</span>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>

                            {/* RECENT ACTIVITY SECTION */}
                            <div className="mt-24">
                                <h6 className="fw-semibold mb-3">Recent Activity</h6>
                                <div className="card border-0 shadow-sm radius-12 overflow-hidden">
                                    <div className="table-responsive">
                                        <Table hover className="table mb-0 text-nowrap align-middle">
                                            <thead className="table-light">
                                                <tr>
                                                    <th className="py-3 px-4 text-secondary-light text-xs text-uppercase">Doctor</th>
                                                    <th className="py-3 px-4 text-secondary-light text-xs text-uppercase">Date</th>
                                                    <th className="py-3 px-4 text-secondary-light text-xs text-uppercase">Status</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {appointments.slice(0, 5).map(appt => (
                                                    <tr key={appt._id}>
                                                        <td className="px-4 py-3">
                                                            <div className="d-flex align-items-center gap-3">
                                                                <div className="w-32-px h-32-px radius-circle bg-primary-50 flex-center text-primary-600 text-sm fw-bold">{getInitials(appt.doctorId?.name)}</div>
                                                                <span className="fw-medium text-primary-light">{appt.doctorId?.name}</span>
                                                            </div>
                                                        </td>
                                                        <td className="px-4 py-3 text-secondary-light">{new Date(appt.date).toLocaleDateString()}</td>
                                                        <td className="px-4 py-3">
                                                            {appt.status === 'Completed' ? <span className="badge bg-success-50 text-success-600 radius-4">Completed</span> :
                                                                appt.status === 'Cancelled' ? <span className="badge bg-danger-50 text-danger-600 radius-4">Cancelled</span> :
                                                                    <span className="badge bg-info-50 text-info-600 radius-4">{appt.status}</span>}
                                                        </td>
                                                    </tr>
                                                ))}
                                                {appointments.length === 0 && (
                                                    <tr>
                                                        <td colSpan="3" className="text-center py-5">
                                                            <div className="text-secondary-light">
                                                                <Icon icon="solar:clipboard-list-linear" className="text-4xl mb-2 opacity-50" />
                                                                <p className="text-sm mb-0">No recent activity found.</p>
                                                            </div>
                                                        </td>
                                                    </tr>
                                                )}
                                            </tbody>
                                        </Table>
                                    </div>
                                </div>
                            </div>
                        </Container>
                    )}

                    {/* VIEW: INVOICES */}
                    {view === 'invoices' && (
                        <div className="card h-100 p-0 radius-12">
                            <div className="card-header border-bottom bg-transparent px-24 py-20 d-flex justify-content-between align-items-center flex-wrap gap-3">
                                <h6 className="mb-0">My Invoices</h6>
                                {/* INVOICE FILTERS */}
                                <div className="d-flex gap-2 flex-wrap">
                                    <div className="input-group input-group-sm w-auto">
                                        <span className="input-group-text bg-white border-end-0"><Icon icon="solar:magnifer-linear" /></span>
                                        <input
                                            type="text"
                                            className="form-control border-start-0 ps-0"
                                            placeholder="Search Invoice # or Doctor"
                                            value={invoiceFilters.search}
                                            onChange={e => setInvoiceFilters({ ...invoiceFilters, search: e.target.value })}
                                            style={{ maxWidth: '220px' }}
                                        />
                                    </div>
                                    <input
                                        type="date"
                                        className="form-control form-control-sm w-auto"
                                        value={invoiceFilters.date}
                                        onChange={e => setInvoiceFilters({ ...invoiceFilters, date: e.target.value })}
                                    />
                                </div>
                            </div>
                            <div className="card-body p-24">
                                <div className="table-responsive">
                                    <Table hover className="table mb-0 text-nowrap align-middle">
                                        <thead className="table-light">
                                            <tr>
                                                <th className="py-3 px-4 text-secondary-light text-xs text-uppercase">Invoice #</th>
                                                <th className="py-3 px-4 text-secondary-light text-xs text-uppercase">Doctor</th>
                                                <th className="py-3 px-4 text-secondary-light text-xs text-uppercase">Date</th>
                                                <th className="py-3 px-4 text-end text-secondary-light text-xs text-uppercase">Amount</th>
                                                <th className="py-3 px-4 text-secondary-light text-xs text-uppercase">Status</th>
                                                <th className="py-3 px-4 text-end text-secondary-light text-xs text-uppercase">Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {invoices
                                                .filter(inv => {
                                                    // Filter Logic (Unified)
                                                    const term = invoiceFilters.search.toLowerCase();
                                                    const matchesSearch = !term ||
                                                        inv.invoiceNumber.toLowerCase().includes(term) ||
                                                        inv.doctorId?.name?.toLowerCase().includes(term);

                                                    if (!matchesSearch) return false;
                                                    if (invoiceFilters.date && new Date(inv.createdAt).toDateString() !== new Date(invoiceFilters.date).toDateString()) return false;
                                                    return true;
                                                })
                                                .map(inv => (
                                                    <tr key={inv._id}>
                                                        <td className="px-4 py-3 fw-medium">{inv.invoiceNumber}</td>
                                                        <td className="px-4 py-3">
                                                            <div className="d-flex align-items-center gap-2">
                                                                <div className="w-32-px h-32-px radius-circle bg-primary-50 flex-center text-primary-600 text-sm fw-bold">{getInitials(inv.doctorId?.name)}</div>
                                                                <span className="fw-medium text-primary-light">Dr. {inv.doctorId?.name.replace("Dr.", "")}</span>
                                                            </div>
                                                        </td>
                                                        <td className="px-4 py-3 text-secondary-light">{new Date(inv.createdAt).toLocaleDateString()}</td>
                                                        <td className="px-4 py-3 text-end fw-bold">{inv.currency} {inv.amount}</td>
                                                        <td className="px-4 py-3">
                                                            <Badge bg={inv.status === 'Paid' ? 'success' : 'warning'} className="px-3 py-2 radius-pill text-xs fw-bold">{inv.status}</Badge>
                                                        </td>
                                                        <td className="px-4 py-3 text-end">
                                                            <div className="d-flex align-items-center justify-content-end gap-2">
                                                                <button onClick={() => handleViewInvoice(inv)} className="btn btn-sm btn-outline-primary radius-8 w-32-px h-32-px flex-center p-0" title="View Invoice">
                                                                    <Icon icon="solar:eye-bold" className="text-lg" />
                                                                </button>
                                                                <button onClick={() => handleEmailInvoice(inv._id)} className="btn btn-sm btn-outline-success radius-8 w-32-px h-32-px flex-center p-0" title="Email Receipt">
                                                                    <Icon icon="solar:letter-bold" className="text-lg" />
                                                                </button>
                                                            </div>
                                                        </td>
                                                    </tr>
                                                ))}
                                            {invoices.length === 0 && (
                                                <tr>
                                                    <td colSpan="6" className="text-center py-5">
                                                        <div className="text-secondary-light">
                                                            <Icon icon="solar:bill-list-linear" className="text-4xl mb-2 opacity-50" />
                                                            <p className="text-sm mb-0">No invoices found.</p>
                                                        </div>
                                                    </td>
                                                </tr>
                                            )}
                                        </tbody>
                                    </Table>
                                </div>
                            </div>
                            {/* Invoice Detail Modal */}
                            <PatientInvoiceDetailModal
                                show={showInvoiceModal}
                                onHide={() => setShowInvoiceModal(false)}
                                invoice={selectedInvoice}
                                onEmail={handleEmailInvoice}
                            />
                        </div>
                    )}

                    {/* VIEW: FIND DOCTOR */}
                    {view === 'find_doctor' && (
                        <Container fluid className="px-0">
                            <div className="card h-100 p-0 radius-12 overflow-hidden mb-24">
                                <div className="card-body p-24">
                                    <div className="row gy-3">
                                        <div className="col-lg-12 mb-3">
                                            <div className="icon-field">
                                                <span className="icon"><Icon icon="solar:magnifer-linear" /></span>
                                                <input type="text" className="form-control radius-8 py-10" placeholder="Search Doctors, Clinics..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} />
                                            </div>
                                        </div>
                                        <div className="col-md-3">
                                            <select className="form-control radius-8" value={filters.specialization} onChange={e => setFilters({ ...filters, specialization: e.target.value })}>
                                                <option value="">All Specialties</option>
                                                {specializations.map(s => <option key={s} value={s}>{s}</option>)}
                                            </select>
                                        </div>
                                        <div className="col-md-3">
                                            <select className="form-control radius-8" value={filters.clinicId} onChange={e => setFilters({ ...filters, clinicId: e.target.value })}>
                                                <option value="">All Clinics</option>
                                                {clinics.map(c => <option key={c._id} value={c._id}>{c.name}</option>)}
                                            </select>
                                        </div>
                                        <div className="col-md-3">
                                            <select className="form-control radius-8" value={filters.location} onChange={e => setFilters({ ...filters, location: e.target.value })}>
                                                <option value="">All Locations</option>
                                                {locations.map(loc => <option key={loc} value={loc}>{loc}</option>)}
                                            </select>
                                        </div>
                                        <div className="col-md-3">
                                            <select className="form-control radius-8" value={filters.experienceMin} onChange={e => setFilters({ ...filters, experienceMin: e.target.value })}>
                                                <option value="0">Any Experience</option>
                                                <option value="5">5+ Years</option>
                                                <option value="10">10+ Years</option>
                                            </select>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="row gy-4">
                                {doctors.map(doc => (
                                    <div className="col-xxl-3 col-xl-4 col-sm-6" key={doc._id}>
                                        <div className="card h-100 radius-12 text-center p-0 hover-scale-1 transition-2">
                                            <div className="card-body p-24 cursor-pointer" onClick={() => { setSelectedDoctor(doc); setShowDoctorDetailsModal(true); }} style={{ cursor: 'pointer' }}>
                                                <div className="w-100-px h-100-px radius-circle bg-primary-50 mx-auto mb-20 flex-center overflow-hidden">
                                                    {doc.profileImage ? <img src={doc.profileImage} alt="" className="w-100 h-100 object-fit-cover" /> : <span className="text-3xl fw-bold text-primary-600">{getInitials(doc.name)}</span>}
                                                </div>
                                                <h5 className="mb-1">{doc.name}</h5>
                                                <span className="text-primary-600 fw-medium mb-12 d-block">{doc.specialization}</span>
                                                <p className="text-secondary-light text-sm mb-20">{doc.experienceYears}+ Years Exp • {doc.clinicId?.address}</p>
                                                <button
                                                    onClick={(e) => { e.stopPropagation(); setSelectedDoctor(doc); setBookingDate(new Date().toISOString().split('T')[0]); setShowBookingModal(true); }}
                                                    className="btn btn-primary-600 radius-8 w-100"
                                                >
                                                    Book Appointment
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                                {doctors.length === 0 && (
                                    <div className="col-12 text-center py-5">
                                        <div className="w-64-px h-64-px bg-neutral-100 radius-circle mx-auto flex-center mb-3 text-secondary-light text-3xl"><Icon icon="solar:sad-circle-linear" /></div>
                                        <h6 className="text-secondary-light">No Doctors Found</h6>
                                        <p className="text-sm text-secondary-light">Try adjusting your filters or search for another clinic.</p>
                                    </div>
                                )}
                            </div>
                        </Container>
                    )}

                    {/* VIEW: APPOINTMENTS */}
                    {view === 'appointments' && (
                        <div className="card h-100 p-0 radius-12">
                            <div className="card-header border-bottom bg-transparent px-24 py-20">
                                <h6 className="mb-0">Appointments</h6>
                            </div>
                            <div className="card-body p-24">
                                {/* FILTERS */}
                                <div className="row g-3 mb-4">
                                    <div className="col-md-3">
                                        <div className="input-group">
                                            <span className="input-group-text bg-white border-end-0"><Icon icon="solar:magnifer-linear" /></span>
                                            <input
                                                type="text"
                                                className="form-control border-start-0 ps-0"
                                                placeholder="Search by Doctor..."
                                                value={myApptFilters.search}
                                                onChange={e => setMyApptFilters({ ...myApptFilters, search: e.target.value })}
                                            />
                                        </div>
                                    </div>
                                    <div className="col-md-3">
                                        <input
                                            type="date"
                                            className="form-control"
                                            value={myApptFilters.date}
                                            onChange={e => setMyApptFilters({ ...myApptFilters, date: e.target.value })}
                                        />
                                    </div>
                                    <div className="col-md-3">
                                        <select
                                            className="form-select"
                                            value={myApptFilters.doctorId}
                                            onChange={e => setMyApptFilters({ ...myApptFilters, doctorId: e.target.value })}
                                        >
                                            <option value="">All Doctors</option>
                                            {[...new Map(appointments.map(item => [item.doctorId?._id, item.doctorId])).values()]
                                                .filter(Boolean)
                                                .map(doc => (
                                                    <option key={doc._id} value={doc._id}>{doc.name}</option>
                                                ))}
                                        </select>
                                    </div>
                                    <div className="col-md-3">
                                        <select
                                            className="form-select"
                                            value={myApptFilters.status}
                                            onChange={e => setMyApptFilters({ ...myApptFilters, status: e.target.value })}
                                        >
                                            <option value="">All Statuses</option>
                                            <option value="Upcoming">Upcoming</option>
                                            <option value="Completed">Completed</option>
                                            <option value="Cancelled">Cancelled</option>
                                            <option value="Missed">Missed</option>
                                        </select>
                                    </div>
                                </div>

                                <Tabs defaultActiveKey="upcoming" className="nav-bordered mb-24">
                                    <Tab eventKey="upcoming" title="Upcoming">
                                        <div className="table-responsive">
                                            <Table hover className="table text-nowrap align-middle">
                                                <thead><tr><th>Doctor</th><th>Date</th><th>Type</th><th>Queue</th><th>Status</th><th>Action</th></tr></thead>
                                                <tbody>
                                                    {appointments.filter(a => ['Upcoming', 'Active', 'Accepted'].includes(a.status))
                                                        .filter(a => {
                                                            if (myApptFilters.search && !a.doctorId?.name?.toLowerCase().includes(myApptFilters.search.toLowerCase())) return false;
                                                            if (myApptFilters.date && new Date(a.date).toDateString() !== new Date(myApptFilters.date).toDateString()) return false;
                                                            if (myApptFilters.doctorId && a.doctorId?._id !== myApptFilters.doctorId) return false;
                                                            if (myApptFilters.status && a.status !== myApptFilters.status) return false;
                                                            return true;
                                                        })
                                                        .length === 0 ? (
                                                        <tr><td colSpan="5" className="text-center py-5"><div className="text-secondary-light"><Icon icon="solar:calendar-mark-linear" className="text-4xl mb-2 opacity-50" /><p>No upcoming appointments found.</p></div></td></tr>
                                                    ) : appointments.filter(a => ['Upcoming', 'Active', 'Accepted'].includes(a.status))
                                                        .filter(a => {
                                                            if (myApptFilters.search && !a.doctorId?.name?.toLowerCase().includes(myApptFilters.search.toLowerCase())) return false;
                                                            if (myApptFilters.date && new Date(a.date).toDateString() !== new Date(myApptFilters.date).toDateString()) return false;
                                                            if (myApptFilters.doctorId && a.doctorId?._id !== myApptFilters.doctorId) return false;
                                                            return true;
                                                        })
                                                        .map(a => (
                                                            <tr key={a._id}>
                                                                <td>
                                                                    <div className="d-flex align-items-center gap-3">
                                                                        <div className="w-40-px h-40-px radius-circle bg-primary-50 flex-center text-primary-600 fw-bold">{getInitials(a.doctorId?.name)}</div>
                                                                        <div><h6 className="text-md mb-0 text-primary-light">{a.doctorId?.name}</h6><span className="text-xs text-secondary-light">{a.clinicId?.name}</span></div>
                                                                    </div>
                                                                </td>
                                                                <td>{new Date(a.date).toLocaleDateString()}<br /><span className="text-xs text-secondary-light">{a.timeSlot}</span></td>
                                                                <td><span className="badge bg-primary-50 text-primary-600 radius-4">{a.type}</span></td>
                                                                <td>#{a.queueNumber}</td>
                                                                <td><span className={`badge radius-4 px-10 py-4 ${a.status === 'Active' ? 'bg-danger-100 text-danger-600' : 'bg-info-100 text-info-600'}`}>{a.status}</span></td>
                                                                <td>
                                                                    {a.status === 'Upcoming' ? (
                                                                        <button className="btn btn-sm btn-outline-danger radius-8 px-3" onClick={() => handleCancel(a._id)}>Cancel</button>
                                                                    ) : (
                                                                        <span className="text-secondary-light text-sm">Not Applicable</span>
                                                                    )}
                                                                </td>
                                                            </tr>
                                                        ))}</tbody>
                                            </Table>
                                        </div>
                                    </Tab>
                                    <Tab eventKey="history" title="History">
                                        <div className="table-responsive">
                                            <Table hover className="table text-nowrap align-middle">
                                                <thead><tr><th>Doctor</th><th>Date</th><th>Status</th><th>Action</th></tr></thead>
                                                <tbody>
                                                    {appointments.filter(a => ['Completed', 'Cancelled', 'Missed'].includes(a.status))
                                                        .filter(a => {
                                                            if (myApptFilters.search && !a.doctorId?.name?.toLowerCase().includes(myApptFilters.search.toLowerCase())) return false;
                                                            if (myApptFilters.date && new Date(a.date).toDateString() !== new Date(myApptFilters.date).toDateString()) return false;
                                                            if (myApptFilters.doctorId && a.doctorId?._id !== myApptFilters.doctorId) return false;
                                                            if (myApptFilters.status && a.status !== myApptFilters.status) return false;
                                                            return true;
                                                        })
                                                        .length === 0 ? (
                                                        <tr><td colSpan="4" className="text-center py-5"><div className="text-secondary-light"><Icon icon="solar:history-linear" className="text-4xl mb-2 opacity-50" /><p>No past appointments found.</p></div></td></tr>
                                                    ) : appointments.filter(a => ['Completed', 'Cancelled', 'Missed'].includes(a.status))
                                                        .filter(a => {
                                                            if (myApptFilters.search && !a.doctorId?.name?.toLowerCase().includes(myApptFilters.search.toLowerCase())) return false;
                                                            if (myApptFilters.date && new Date(a.date).toDateString() !== new Date(myApptFilters.date).toDateString()) return false;
                                                            if (myApptFilters.doctorId && a.doctorId?._id !== myApptFilters.doctorId) return false;
                                                            if (myApptFilters.status && a.status !== myApptFilters.status) return false;
                                                            return true;
                                                        })
                                                        .map(a => (
                                                            <tr key={a._id}>
                                                                <td>
                                                                    <div className="d-flex align-items-center gap-3">
                                                                        <div className="w-32-px h-32-px radius-circle bg-primary-50 flex-center text-primary-600 fw-bold">{getInitials(a.doctorId?.name)}</div>
                                                                        <div>
                                                                            <h6 className="text-md mb-0 text-primary-light">{a.doctorId?.name}</h6>
                                                                            <p className="text-xs text-secondary-light mb-0">{a.clinicId?.name}</p>
                                                                        </div>
                                                                    </div>
                                                                </td>
                                                                <td>{new Date(a.date).toLocaleDateString()}</td>
                                                                <td><span className={`badge radius-4 ${a.status === 'Completed' ? 'bg-success-100 text-success-600' : 'bg-danger-100 text-danger-600'}`}>{a.status}</span></td>
                                                                <td>
                                                                    {a.status === 'Completed' ? (
                                                                        <button className="btn btn-sm btn-outline-primary-600 radius-8" onClick={() => fetchPrescription(a._id)}>View Rx</button>
                                                                    ) : (['Cancelled', 'Missed'].includes(a.status)) ? (
                                                                        <button
                                                                            className="btn btn-sm btn-primary-600 radius-8 px-3 d-flex align-items-center gap-1"
                                                                            onClick={(e) => {
                                                                                e.stopPropagation();
                                                                                // Redirect to Reschedule Needed View
                                                                                navigate('?view=reschedule_needed');
                                                                            }}
                                                                        >
                                                                            <Icon icon="solar:restart-bold" /> Reschedule
                                                                        </button>
                                                                    ) : (
                                                                        <span className="text-secondary-light text-sm">Not Applicable</span>
                                                                    )}
                                                                </td>
                                                            </tr>
                                                        ))}</tbody>
                                            </Table>
                                        </div>
                                    </Tab>
                                </Tabs>
                            </div>
                        </div>
                    )}


                    {/* VIEW: RESCHEDULE NEEDED */}
                    {view === 'reschedule_needed' && (
                        <div className="card h-100 p-0 radius-12">
                            <div className="card-header border-bottom bg-transparent px-24 py-20 bg-danger-50">
                                <h6 className="mb-0 text-danger-700 fw-bold d-flex align-items-center gap-2">
                                    <Icon icon="solar:calendar-add-bold" /> Rescheduling Needed
                                </h6>
                            </div>
                            <div className="card-body p-24">
                                <div className="alert alert-warning border-0 bg-warning-50 text-warning-800 d-flex align-items-start gap-3 mb-4">
                                    <Icon icon="solar:info-circle-bold" className="text-xl mt-1 flex-shrink-0" />
                                    <div>
                                        <h6 className="fw-bold mb-1">Action Required</h6>
                                        <p className="mb-0 text-sm">The following appointments were cancelled due to doctor/patient unavailability. Please reschedule them at your earliest convenience.</p>
                                    </div>
                                </div>

                                {/* RESCHEDULE FILTERS (Refined: Removed Time, 50-50 Layout) */}
                                <div className="row g-3 mb-4">
                                    <div className="col-md-6">
                                        <div className="input-group h-100">
                                            <span className="input-group-text bg-white border-end-0"><Icon icon="solar:stethoscope-linear" /></span>
                                            <input
                                                type="text"
                                                className="form-control border-start-0 ps-0"
                                                placeholder="Filter by Doctor..."
                                                value={rescheduleFilters.doctor}
                                                onChange={e => setRescheduleFilters({ ...rescheduleFilters, doctor: e.target.value })}
                                            />
                                        </div>
                                    </div>
                                    <div className="col-md-6">
                                        <div className="input-group h-100">
                                            <span className="input-group-text bg-white border-end-0"><Icon icon="solar:calendar-linear" /></span>
                                            <input
                                                type="date"
                                                className="form-control border-start-0 ps-0"
                                                value={rescheduleFilters.date}
                                                onChange={e => setRescheduleFilters({ ...rescheduleFilters, date: e.target.value })}
                                            />
                                        </div>
                                    </div>
                                </div>

                                <div className="row g-4">
                                    {appointments.filter(a => {
                                        // 1. Core Status Filter
                                        const needsReschedule = (a.status === 'Cancelled' && a.rescheduleStatus === 'Pending') || (a.status === 'Missed' && a.rescheduleStatus !== 'Completed');
                                        if (!needsReschedule) return false;

                                        // 2. User Filters (Time Removed)
                                        const matchDoc = rescheduleFilters.doctor ? a.doctorId?.name?.toLowerCase().includes(rescheduleFilters.doctor.toLowerCase()) : true;
                                        const matchDate = rescheduleFilters.date ? new Date(a.date).toLocaleDateString() === new Date(rescheduleFilters.date).toLocaleDateString() : true;

                                        return matchDoc && matchDate;
                                    }).length === 0 ? (
                                        <div className="col-12 text-center py-5">
                                            <div className="w-64-px h-64-px bg-success-50 radius-circle mx-auto flex-center mb-3 text-success-600 text-3xl">
                                                <Icon icon="solar:check-circle-bold" />
                                            </div>
                                            <h6 className="text-dark">No Matching Appointments</h6>
                                            <p className="text-secondary-light">No appointments found matching your filters or requiring reschedule.</p>
                                        </div>
                                    ) : (
                                        appointments.filter(a => {
                                            // Same Filter Logic
                                            const needsReschedule = (a.status === 'Cancelled' && a.rescheduleStatus === 'Pending') || (a.status === 'Missed' && a.rescheduleStatus !== 'Completed');
                                            if (!needsReschedule) return false;

                                            const matchDoc = rescheduleFilters.doctor ? a.doctorId?.name?.toLowerCase().includes(rescheduleFilters.doctor.toLowerCase()) : true;
                                            const matchDate = rescheduleFilters.date ? new Date(a.date).toLocaleDateString() === new Date(rescheduleFilters.date).toLocaleDateString() : true;

                                            return matchDoc && matchDate;
                                        }).map(appt => (
                                            <div className="col-md-6 col-lg-4" key={appt._id}>
                                                <div className="card h-100 border border-danger-100 shadow-sm radius-12 hover-scale-1 transition-2">
                                                    <div className="card-body p-3">
                                                        <div className="d-flex align-items-center gap-3 mb-3 border-bottom pb-3 border-dashed">
                                                            <div className="w-48-px h-48-px radius-circle bg-danger-50 flex-center text-danger-600 fw-bold">
                                                                {getInitials(appt.doctorId?.name)}
                                                            </div>
                                                            <div>
                                                                <h6 className="text-md mb-0 text-dark fw-bold">{appt.doctorId?.name}</h6>
                                                                <span className="text-xs text-secondary-light">{appt.clinicId?.name}</span>
                                                            </div>
                                                        </div>
                                                        <div className="mb-3">
                                                            <p className="text-xs text-secondary mb-1">Original Slot</p>
                                                            <div className="d-flex align-items-center gap-2 text-dark fw-medium">
                                                                <Icon icon="solar:calendar-date-broken" className="text-primary-500" />
                                                                {new Date(appt.date).toLocaleDateString()}
                                                                <span className="text-secondary-light">|</span>
                                                                {appt.timeSlot}
                                                            </div>
                                                        </div>
                                                        <div className="bg-danger-50 p-2 radius-8 mb-3">
                                                            <p className="text-xs text-danger-700 mb-0 fw-medium">
                                                                Reason: {(() => {
                                                                    if (appt.status === 'Missed') return 'Missed the appointment';
                                                                    if (appt.cancellationReason && (appt.cancellationReason.toLowerCase().includes('user') || appt.cancellationReason.toLowerCase().includes('me'))) return 'Cancelled by me';
                                                                    if (appt.cancellationReason && (appt.cancellationReason.toLowerCase().includes('available') || appt.cancellationReason.toLowerCase().includes('doctor'))) return 'Doctor unavailable for that day';
                                                                    return appt.cancellationReason || 'Rescheduling Required';
                                                                })()}
                                                            </p>
                                                        </div>
                                                        <button
                                                            className="btn btn-primary-600 w-100 radius-8 fw-bold py-2"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                const clinicInfo = typeof appt.clinicId === 'string' ? { _id: appt.clinicId } : appt.clinicId;
                                                                const docForBooking = { ...appt.doctorId, clinicId: clinicInfo };
                                                                setSelectedDoctor(docForBooking);
                                                                setBookingDate(new Date().toISOString().split('T')[0]);
                                                                setRescheduleApptId(appt._id); // Set ID for tracking
                                                                setShowBookingModal(true);
                                                            }}
                                                        >
                                                            Reschedule Now
                                                        </button>
                                                    </div>
                                                </div>
                                            </div>
                                        ))
                                    )}
                                </div>
                            </div>
                        </div>
                    )}


                    {/* VIEW: MEDICAL RECORDS */}
                    {view === 'records' && (
                        <div className="card h-100 p-0 radius-12">
                            <div className="card-header border-bottom bg-transparent px-24 py-20">
                                <h6 className="mb-0">Medical Records</h6>
                            </div>
                            <div className="card-body p-24">
                                {/* RECORDS FILTERS */}
                                <div className="row g-3 mb-4">
                                    <div className="col-md-6">
                                        <div className="input-group">
                                            <span className="input-group-text bg-white border-end-0"><Icon icon="solar:magnifer-linear" /></span>
                                            <input
                                                type="text"
                                                className="form-control border-start-0 ps-0"
                                                placeholder="Filter by Doctor Name..."
                                                value={recordsFilters.search}
                                                onChange={e => setRecordsFilters({ ...recordsFilters, search: e.target.value })}
                                            />
                                        </div>
                                    </div>
                                    <div className="col-md-6">
                                        <input
                                            type="date"
                                            className="form-control"
                                            value={recordsFilters.date}
                                            onChange={e => setRecordsFilters({ ...recordsFilters, date: e.target.value })}
                                        />
                                    </div>
                                </div>

                                <div className="table-responsive">
                                    <Table hover className="table text-nowrap align-middle">
                                        <thead className="table-light"><tr><th>Doctor</th><th>Date</th><th>Diagnosis/Notes</th><th>Action</th></tr></thead>
                                        <tbody>
                                            {appointments
                                                .filter(a => a.status === 'Completed')
                                                .filter(a => {
                                                    if (recordsFilters.search && !a.doctorId?.name?.toLowerCase().includes(recordsFilters.search.toLowerCase())) return false;
                                                    if (recordsFilters.date && new Date(a.date).toDateString() !== new Date(recordsFilters.date).toDateString()) return false;
                                                    return true;
                                                })
                                                .length === 0 ? (
                                                <tr>
                                                    <td colSpan="4" className="text-center py-5">
                                                        <div className="text-secondary-light">
                                                            <Icon icon="solar:folder-error-linear" className="text-6xl mb-3 opacity-50" />
                                                            <h6 className="text-secondary mb-1">No Records Found</h6>
                                                            <p className="text-sm mb-0">Try adjusting your filters.</p>
                                                        </div>
                                                    </td>
                                                </tr>
                                            ) : appointments
                                                .filter(a => a.status === 'Completed')
                                                .filter(a => {
                                                    if (recordsFilters.search && !a.doctorId?.name?.toLowerCase().includes(recordsFilters.search.toLowerCase())) return false;
                                                    if (recordsFilters.date && new Date(a.date).toDateString() !== new Date(recordsFilters.date).toDateString()) return false;
                                                    return true;
                                                })
                                                .map(a => (
                                                    <tr key={a._id}>
                                                        <td>
                                                            <div className="d-flex align-items-center gap-3">
                                                                <div className="w-40-px h-40-px radius-circle bg-primary-50 flex-center text-primary-600 fw-bold">{getInitials(a.doctorId?.name)}</div>
                                                                <div><h6 className="text-md mb-0 text-primary-light">{a.doctorId?.name}</h6><span className="text-xs text-secondary-light">{a.clinicId?.name}</span></div>
                                                            </div>
                                                        </td>
                                                        <td>{new Date(a.date).toLocaleDateString()}</td>
                                                        <td><span className="text-secondary-light"> Check Prescription</span></td>
                                                        <td><button className="btn btn-sm btn-outline-primary-600 radius-8" onClick={() => fetchPrescription(a._id)}>View Rx</button></td>
                                                    </tr>
                                                ))}
                                        </tbody>
                                    </Table>
                                </div>
                            </div>
                        </div>
                    )}


                    {/* VIEW: PROFILE */}
                    {view === 'profile' && (
                        <Container fluid className="p-4">
                            <div className="row justify-content-center">
                                <Col xxl={9} xl={10}>
                                    <div className="card h-100 p-3 radius-16 border-0 shadow-sm overflow-hidden bg-white">
                                        <div className="card-header border-bottom bg-transparent px-32 py-24 d-flex align-items-center gap-3">
                                            <div className="w-12-px h-12-px bg-primary-600 radius-circle"></div>
                                            <div>
                                                <h5 className="mb-0 fw-bold text-primary-900">Account Settings</h5>
                                                <p className="text-secondary-light text-sm mb-0">Manage your profile details and security settings</p>
                                            </div>
                                        </div>
                                        <div className="d-flex flex-wrap align-items-center gap-4 mb-48 bg-primary-50 p-24 radius-12 border border-primary-100">
                                            <div className="w-80-px h-80-px radius-circle bg-white flex-center text-3xl fw-bold text-primary-600 border border-4 border-white shadow-sm overflow-hidden">
                                                {user?.profileImage ? (
                                                    <img src={user.profileImage} alt="Profile" className="w-100 h-100 object-fit-cover" />
                                                ) : (
                                                    getInitials(user?.name)
                                                )}
                                            </div>
                                            <div className="flex-grow-1">
                                                <h4 className="fw-bold text-primary-900 mb-1">{user?.name}</h4>
                                                <div className="d-flex align-items-center gap-3 text-secondary-light text-sm">
                                                    <span className="d-flex align-items-center gap-1"><Icon icon="solar:letter-linear" /> {user?.email}</span>
                                                    <span className="d-flex align-items-center gap-1"><Icon icon="solar:phone-calling-linear" /> {user?.phone || 'No phone added'}</span>
                                                </div>
                                            </div>
                                        </div>

                                        <Row className="g-5">
                                            <Col lg={7}>
                                                <h6 className="fw-bold text-primary-900 mb-4 pb-2 border-bottom">Personal Information</h6>
                                                <form onSubmit={handleUpdateProfile}>
                                                    <div className="row gy-4">
                                                        <div className="col-md-6">
                                                            <label className="form-label fw-semibold text-secondary-light text-sm">Full Name</label>
                                                            <input type="text" className="form-control radius-10 py-2.5 px-3 bg-neutral-50 border-neutral-200 focus-primary" value={profileForm.name} onChange={e => setProfileForm({ ...profileForm, name: e.target.value })} />
                                                        </div>
                                                        <div className="col-md-6">
                                                            <label className="form-label fw-semibold text-secondary-light text-sm">Phone Number</label>
                                                            <input type="text" className="form-control radius-10 py-2.5 px-3 bg-neutral-50 border-neutral-200 focus-primary" value={profileForm.phone} onChange={e => setProfileForm({ ...profileForm, phone: e.target.value })} />
                                                        </div>
                                                        <div className="col-12">
                                                            <label className="form-label fw-semibold text-secondary-light text-sm">Email Address</label>
                                                            <div className="input-group">
                                                                <span className="input-group-text bg-neutral-100 border-end-0 radius-start-10 text-secondary-light"><Icon icon="solar:letter-bold" /></span>
                                                                <input type="email" className="form-control radius-end-10 py-2.5 bg-neutral-100 border-neutral-200 text-secondary" value={profileForm.email} disabled />
                                                            </div>
                                                        </div>
                                                        <div className="col-12 text-end">
                                                            <button type="submit" className="btn btn-primary-600 radius-10 px-4 py-2.5 fw-medium d-inline-flex align-items-center gap-2">
                                                                <Icon icon="solar:diskette-bold" /> Save Changes
                                                            </button>
                                                        </div>
                                                    </div>
                                                </form>
                                            </Col>
                                            <Col lg={5}>
                                                <div className="bg-neutral-50 p-24 radius-16 border border-neutral-100 h-100">
                                                    <h6 className="fw-bold text-primary-900 mb-4 pb-2 border-bottom">Security</h6>
                                                    <form onSubmit={handleChangePassword}>
                                                        <div className="mb-3">
                                                            <label className="form-label fw-semibold text-secondary-light text-sm">Current Password</label>
                                                            <div className="input-group">
                                                                <span className="input-group-text bg-white border-end-0 radius-start-10 text-secondary-light"><Icon icon="solar:lock-password-linear" /></span>
                                                                <input type="password" className="form-control border-start-0 radius-end-10 py-2.5 focus-primary" placeholder="••••••••" value={passwordForm.currentPassword} onChange={e => setPasswordForm({ ...passwordForm, currentPassword: e.target.value })} required />
                                                            </div>
                                                        </div>
                                                        <div className="mb-3">
                                                            <label className="form-label fw-semibold text-secondary-light text-sm">New Password</label>
                                                            <div className="input-group">
                                                                <span className="input-group-text bg-white border-end-0 radius-start-10 text-secondary-light"><Icon icon="solar:key-minimalistic-linear" /></span>
                                                                <input type="password" className="form-control border-start-0 radius-end-10 py-2.5 focus-primary" placeholder="••••••••" value={passwordForm.newPassword} onChange={e => setPasswordForm({ ...passwordForm, newPassword: e.target.value })} required />
                                                            </div>
                                                        </div>
                                                        <div className="mb-4">
                                                            <label className="form-label fw-semibold text-secondary-light text-sm">Confirm Password</label>
                                                            <div className="input-group">
                                                                <span className="input-group-text bg-white border-end-0 radius-start-10 text-secondary-light"><Icon icon="solar:check-circle-linear" /></span>
                                                                <input type="password" className="form-control border-start-0 radius-end-10 py-2.5 focus-primary" placeholder="••••••••" value={passwordForm.confirmPassword} onChange={e => setPasswordForm({ ...passwordForm, confirmPassword: e.target.value })} required />
                                                            </div>
                                                        </div>
                                                        <button type="submit" className="btn btn-outline-danger radius-10 w-100 py-2.5 fw-medium d-inline-flex align-items-center justify-content-center gap-2">
                                                            <Icon icon="solar:shield-keyhole-bold" /> Update Password
                                                        </button>
                                                    </form>
                                                </div>
                                            </Col>
                                        </Row>
                                    </div>
                                </Col>
                            </div>
                        </Container>
                    )}

                    {/* VIEW: SYMPTOM CHECKER */}
                    {view === 'symptom_checker' && (
                        <Container fluid className="px-0">
                            <SymptomChecker onFindDoctor={handleFindDoctorForSymptom} />
                        </Container>
                    )}
                </div>

                {/* MODALS */}
                <Modal show={showBookingModal} onHide={() => { setShowBookingModal(false); setRescheduleApptId(null); }} centered size="lg" contentClassName="radius-16 border-0">
                    <div className="modal-content radius-16 border-0">
                        <div className="modal-body p-32">
                            <div className="d-flex justify-content-between align-items-center mb-24">
                                <h5 className="mb-0">{rescheduleApptId ? 'Reschedule Appointment' : 'Book Appointment'}</h5>
                                <button onClick={() => { setShowBookingModal(false); setRescheduleApptId(null); }} className="btn-close"></button>
                            </div>

                            {selectedDoctor && (
                                <div className="mb-3">
                                    <label className="form-label fw-semibold">Select Doctor</label>
                                    <select
                                        className="form-select radius-8"
                                        value={selectedDoctor._id}
                                        onChange={(e) => {
                                            const doc = doctors.find(d => d._id === e.target.value);
                                            setSelectedDoctor(doc);
                                            setBookingDate(''); // Reset date to force re-selection/refresh
                                            setAvailableSlots([]);
                                        }}
                                    >
                                        <option value={selectedDoctor._id}>{selectedDoctor.name} (Selected)</option>
                                        {doctors
                                            .filter(d => d.clinicId?._id === selectedDoctor.clinicId?._id && d._id !== selectedDoctor._id)
                                            .map(d => (
                                                <option key={d._id} value={d._id}>{d.name} - {d.specialization}</option>
                                            ))}
                                    </select>
                                    <div className="form-text">You can change the doctor within the same clinic.</div>
                                    <div className="text-end mt-1">
                                        <button
                                            type="button"
                                            onClick={() => handleViewMap(selectedDoctor.clinicId?.mapUrl)}
                                            className="btn btn-link p-0 text-primary-600 text-sm fw-medium d-inline-flex align-items-center gap-1 hover-scale-sm text-decoration-none"
                                        >
                                            <Icon icon="solar:map-point-bold" /> View on Map
                                        </button>
                                    </div>
                                </div>
                            )}
                            <Form onSubmit={handleBooking}>
                                <div className="row gy-3 mb-24">
                                    <div className="col-md-6">
                                        <label className="form-label fw-semibold">Date</label>
                                        <input type="date" className="form-control radius-8" value={bookingDate} min={new Date().toISOString().split('T')[0]} onChange={e => setBookingDate(e.target.value)} />
                                    </div>
                                    <div className="col-md-6">
                                        <label className="form-label fw-semibold">Type</label>
                                        <div className="d-flex gap-2">
                                            {['Physical', 'Online'].map(t => (
                                                <button key={t} type='button' className={`btn w-50 radius-8 ${consultationType === t ? 'btn-primary-600' : 'btn-outline-secondary'}`} onClick={() => setConsultationType(t)}>{t}</button>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                                <div className="mb-24">
                                    <label className="form-label fw-semibold mb-3">Available Slots</label>
                                    <div className="d-flex flex-wrap gap-2">
                                        {isLoadingSlots ? <span className="text-secondary-light">Loading slots...</span> : availableSlots.length === 0 ? (
                                            <div className="w-100 text-center py-3 bg-neutral-50 radius-8 border border-neutral-200">
                                                <span className="text-danger-600 fw-medium">No slots available for this date.</span>
                                                <p className="text-xs text-secondary-light mb-0 mt-1">The doctor might not be working or all slots are booked.</p>
                                            </div>
                                        ) : availableSlots.map(slot => (
                                            <button
                                                key={slot} type="button"
                                                className={`btn radius-4 px-12 py-6 text-sm ${selectedSlot === slot ? 'btn-primary-600' : 'btn-outline-primary text-primary-600'}`}
                                                onClick={() => setSelectedSlot(slot)}
                                            >
                                                {slot}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                                <div className="d-flex justify-content-between align-items-center mb-3 p-3 bg-light rounded-3">
                                    <span className="fw-bold text-secondary">Consultation Fee</span>
                                    <span className="fw-bolder text-primary h5 mb-0">
                                        {(() => {
                                            let basePrice = consultationType === 'Online' ? 300 : 500;
                                            if (rescheduleApptId) {
                                                const oldAppt = appointments.find(a => a._id === rescheduleApptId);
                                                const isDoctorFault = oldAppt && oldAppt.cancellationReason && (
                                                    oldAppt.cancellationReason.toLowerCase().includes('doctor') ||
                                                    oldAppt.cancellationReason.toLowerCase().includes('available')
                                                );
                                                if (isDoctorFault) return <span className="text-success">Free (Waived)</span>;
                                                return <span>₹ {basePrice / 2} <span className="text-muted text-sm fw-normal">(Reschedule Fee)</span></span>;
                                            }
                                            return `₹ ${basePrice}`;
                                        })()}
                                    </span>
                                </div>
                                <button type="submit" className="btn btn-primary-600 radius-8 w-100 py-12" disabled={!selectedSlot}>
                                    <Icon icon="solar:card-bold" className="me-2" />
                                    {rescheduleApptId ? (() => {
                                        const oldAppt = appointments.find(a => a._id === rescheduleApptId);
                                        const isDoctorFault = oldAppt && oldAppt.cancellationReason && (
                                            oldAppt.cancellationReason.toLowerCase().includes('doctor') ||
                                            oldAppt.cancellationReason.toLowerCase().includes('available')
                                        );
                                        return isDoctorFault ? 'Confirm Free Reschedule' : 'Pay Half & Reschedule';
                                    })() : 'Pay & Confirm Booking'}
                                </button>
                            </Form>
                        </div>
                    </div>
                </Modal>


                <Modal show={showRxModal} onHide={() => setShowRxModal(false)} size="lg" centered contentClassName="radius-16">
                    <Modal.Header closeButton><Modal.Title>Prescription</Modal.Title></Modal.Header>
                    <Modal.Body className="printable-content">
                        {viewRx && (
                            <div>
                                <div className="d-flex justify-content-between mb-4">
                                    <div><h5 className="mb-0">Dr. {viewRx.doctorId?.name.replace("Dr.", "")?.replace("Mr.", "")?.replace("Mrs.", "")?.replace("dr.", "")}</h5><p className="text-secondary-light">{viewRx.clinicId?.name}</p></div>
                                    <div className='text-end'><span className="text-secondary-light">Date</span><h6 className='mb-0'>{new Date(viewRx.createdAt).toLocaleDateString()}</h6></div>
                                </div>
                                <div className="table-responsive">
                                    <Table bordered>
                                        <thead className="bg-primary-50"><tr><th>Medicine</th><th>Dosage</th><th>Freq</th><th>Duration</th></tr></thead>
                                        <tbody>
                                            {viewRx.medicines && viewRx.medicines.length > 0 ? (
                                                viewRx.medicines.map((m, i) => <tr key={i}><td>{m.name}</td><td>{m.dosage}</td><td>{m.frequency}</td><td>{m.duration || "15 days"}</td></tr>)
                                            ) : <tr><td colSpan="4" className="text-center text-secondary">No medicines listed (Check notes or image)</td></tr>}
                                        </tbody>
                                    </Table>
                                </div>
                                {viewRx.notes && <div className="bg-warning-50 text-warning-600 p-3 radius-8 mt-3"><strong>Note:</strong> {viewRx.notes}</div>}

                                {viewRx.imageUrl && (
                                    <div className="mt-4 pt-3 border-top">
                                        <h6 className="mb-3">Prescription Image</h6>
                                        <div className="text-center bg-light p-3 rounded">
                                            <img src={viewRx.imageUrl} alt="Rx" className="img-fluid rounded shadow-sm" style={{ maxHeight: '400px' }} />
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}
                    </Modal.Body>
                    <Modal.Footer><button className="btn btn-secondary radius-8" onClick={() => window.print()}>Print</button></Modal.Footer>
                </Modal>

                {/* PROFILE MODAL REMOVED */}

                {/* DOCTOR DETAILS MODAL */}
                <Modal show={showDoctorDetailsModal} onHide={() => setShowDoctorDetailsModal(false)} centered size="md" contentClassName="radius-16">
                    <Modal.Header closeButton><Modal.Title className="fw-bold">Doctor Details</Modal.Title></Modal.Header>
                    <Modal.Body className="text-center p-24">
                        {selectedDoctor && (
                            <>
                                <div className="w-100-px h-100-px radius-circle bg-primary-50 mx-auto mb-20 flex-center overflow-hidden border border-4 border-white shadow-sm">
                                    {selectedDoctor.profileImage ? <img src={selectedDoctor.profileImage} alt="" className="w-100 h-100 object-fit-cover" /> : <span className="text-4xl fw-bold text-primary-600">{getInitials(selectedDoctor.name)}</span>}
                                </div>
                                <h4 className="mb-1 fw-bold">{selectedDoctor.name}</h4>
                                <p className="text-primary-600 fw-medium mb-3">{selectedDoctor.specialization}</p>

                                <div className="bg-neutral-50 p-3 radius-12 text-start mb-4">
                                    <div className="d-flex justify-content-between mb-2">
                                        <span className="text-secondary-light">Experience</span>
                                        <span className="fw-semibold">{selectedDoctor.experienceYears} Years</span>
                                    </div>
                                    <div className="d-flex justify-content-between mb-2">
                                        <span className="text-secondary-light">Clinic</span>
                                        <span className="fw-semibold">{selectedDoctor.clinicId?.name}</span>
                                    </div>
                                    <div className="d-flex justify-content-between">
                                        <span className="text-secondary-light">Location</span>
                                        <span className="fw-semibold">{selectedDoctor.clinicId?.address}</span>
                                    </div>
                                    <div className="text-end mt-1">
                                        <button
                                            type="button"
                                            onClick={() => handleViewMap(selectedDoctor.clinicId?.mapUrl)}
                                            className="btn btn-link p-0 text-primary-600 text-sm fw-medium d-inline-flex align-items-center gap-1 hover-scale-sm text-decoration-none"
                                        >
                                            <Icon icon="solar:map-point-bold" /> View on Map
                                        </button>
                                    </div>
                                    {selectedDoctor.bio && (
                                        <div className="mt-3 pt-3 border-top">
                                            <p className="text-sm text-secondary mb-0">{selectedDoctor.bio}</p>
                                        </div>
                                    )}
                                </div>

                                <button
                                    onClick={() => { setShowDoctorDetailsModal(false); setBookingDate(new Date().toISOString().split('T')[0]); setShowBookingModal(true); }}
                                    className="btn btn-primary-600 radius-8 w-100 py-12"
                                >
                                    Book Appointment
                                </button>
                            </>
                        )}
                    </Modal.Body>
                </Modal>

                <style>{`
                    @media print {
                        body * { visibility: hidden; }
                        .modal.show { visibility: visible; position: absolute; left: 0; top: 0; width: 100%; margin: 0; }
                        .modal.show .modal-dialog { width: 100%; max-width: 100%; margin: 0; }
                        .modal.show .modal-content { border: none; box-shadow: none; }
                        .modal.show .modal-body { padding: 20px; }
                        .modal.show .modal-body * { visibility: visible; }
                        .modal-footer, .btn-close, .modal-header { display: none !important; } 
                        /* Re-show header title for context if needed, but usually body is enough */
                    }
                `}</style>
            </main >
        </section >
    );
};

export default PatientDashboard;
